import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import LimitMeter, { type Limit } from '../components/LimitMeter'

const KIND_META: Record<string, { icon: string; label: string }> = {
  bug: { icon: '🐛', label: 'Report a bug' },
  feature: { icon: '✨', label: 'Request a feature' },
  question: { icon: '💬', label: 'Ask a question' },
  report_content: { icon: '🚩', label: 'Report content or a member' },
  report_ai: { icon: '🚩', label: 'Report a Sage response' },
}
const STATUS_STYLE: Record<string, string> = {
  new: 'bg-white/10 text-slate-300', reviewing: 'bg-rootv-500/15 text-rootv-400',
  approved: 'bg-gold-500/15 text-gold-300', queued: 'bg-teal-500/15 text-teal-300',
  declined: 'bg-red-500/10 text-red-400', closed: 'bg-white/5 text-slate-500',
}

export default function Support() {
  const [tickets, setTickets] = useState<Awaited<ReturnType<typeof api.listMyTickets>>>([])
  const [kind, setKind] = useState('bug')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  // a refusal rendered in the same gold as a confirmation reads as success (D-144)
  const [failed, setFailed] = useState(false)
  const [room, setRoom] = useState<{ limit: Limit; plan: string; purchases: boolean } | null>(null)
  const loadRoom = () => api.getHeadroom()
    .then(h => { const l = h.limits.find(x => x.key === 'tickets'); if (l) setRoom({ limit: l, plan: h.plan, purchases: h.purchases_enabled }) })
    .catch(() => {})
  useEffect(() => { loadRoom() }, [])

  const load = () => api.listMyTickets().then(setTickets).catch(() => {})
  useEffect(() => { load() }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setMsg(''); setFailed(false)
    try {
      const res = await api.fileTicket(kind, subject.trim(), body.trim())
      setMsg(res.message); loadRoom()
      setSubject(''); setBody('')
      load()
    } catch (e) {
      setFailed(true)
      setMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Could not submit')
    } finally { setBusy(false) }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-bold text-slate-100 mb-1">Help & Support</h1>
      <p className="text-sm text-slate-500 mb-8">Report a bug, request a feature, or ask a question. Report a bug, request a feature, ask a question, or flag content, a member, or a Sage response for review. Abuse reports are reviewed within 72 hours.</p>

      <form onSubmit={submit} className="card-glass p-6 mb-8">
        <div className="flex gap-2 mb-4">
          {Object.entries(KIND_META).map(([k, m]) => (
            <button key={k} type="button" onClick={() => setKind(k)}
              className={`flex-1 px-3 py-2 rounded-xl text-sm border transition-colors ${kind === k ? 'border-gold-500/50 bg-gold-500/10 text-gold-200' : 'border-white/10 text-slate-400 hover:border-white/25'}`}>
              {m.icon} {m.label}
            </button>
          ))}
        </div>
        <input required value={subject} onChange={e => setSubject(e.target.value)} maxLength={160}
          placeholder={kind === 'bug' ? 'What went wrong?' : kind === 'feature' ? 'What would you love to see?' : kind === 'report_content' ? 'What content or member, and why? Include the tree/person if you can.' : kind === 'report_ai' ? 'What did Sage say, and what was wrong with it?' : 'Your question'}
          className="input-dark mb-3" />
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={4} maxLength={4000}
          placeholder="Details (steps to reproduce, what you expected, etc.)" className="input-dark mb-4 resize-none" />
        <button type="submit" disabled={busy || !subject.trim()} className="btn-gold btn-shine">
          {busy ? 'Sending…' : 'Send to the keeper'}
        </button>
        {room && <LimitMeter limit={room.limit} plan={room.plan} purchasesEnabled={room.purchases} />}
        {msg && (
          <p className={`text-xs mt-3 ${failed ? 'text-red-300' : 'text-gold-300'}`}>
            {failed ? '⚠ ' : ''}{msg}
          </p>
        )}
      </form>

      {tickets.length > 0 && (
        <>
          <h2 className="font-display text-lg font-semibold text-slate-100 mb-3">Your reports</h2>
          <div className="space-y-2">
            {tickets.map(t => (
              <div key={t.id} className="card-glass p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-100">{KIND_META[t.kind]?.icon} {t.subject}</p>
                    {t.body && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.body}</p>}
                    {t.admin_note && <p className="text-xs text-gold-300 mt-1.5">Keeper: {t.admin_note}</p>}
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_STYLE[t.status] || STATUS_STYLE.new}`}>{t.status}</span>
                </div>
                <p className="text-[10px] text-slate-600 mt-2">{t.created_at.slice(0, 16).replace('T', ' ')}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
